import React, { useContext, useEffect, useState } from "react";
import styles from '../../styles/cr.module.scss';
import { CrDatePicker } from "../cr/CrDatePicker";
import { FormButtons } from "../cr/FormButtons";
import { FieldErrorMessage, FieldSuccessMessage } from "../cr/FieldDecorators";
import { DataContext } from "../DataContext";
import { DateService } from "../../services/DateService";
import { ICustomerFinancialYear } from "../../types";

interface CustomerFinancialYearProps {
    customerFinancialYear: ICustomerFinancialYear;
    setCustomerFinancialYear: React.Dispatch<React.SetStateAction<ICustomerFinancialYear>>;
}

interface IErrorMessage {
    FYStartDate: string;
    FYEndDate: string;
}

export class ErrorMessage implements IErrorMessage {
    public FYStartDate = null;
    public FYEndDate = null;
}

export const CustomerFinancialYear = (
    props: CustomerFinancialYearProps
): React.ReactElement => {

    const {
        dataServices: { accountService }
    } = useContext(DataContext);

    const [financialYear, setFinancialYear] =
        useState<ICustomerFinancialYear>({
            fyStartDate: null,
            fyEndDate: null
        });

    const [errMessages, setErrMessages] =
        useState<IErrorMessage>(new ErrorMessage());

    const [saveErrorMsg, setSaveErrorMsg] = useState<string>('');
    const [saveSuccessMsg, setSaveSuccessMsg] = useState<string>('');

    useEffect(() => {
        // const main = document.querySelector('.main');
        // if (main) main.scrollTop = 0;

        (async () => {
            const fy: ICustomerFinancialYear =
                await accountService.getCustomerFinancialYear();

            setFinancialYear(fy);
        })();
    }, []);

    const validateEntity = (): boolean => {

        let returnVal = true;
        const errMsgs = { ...errMessages };

        if (financialYear.fyStartDate == null) {
            errMsgs.FYStartDate = "Start Date is required";
            returnVal = false;
        }
        else {
            errMsgs.FYStartDate = null;
        }

        if (financialYear.fyEndDate == null) {
            errMsgs.FYEndDate = "End Date is required";
            returnVal = false;
        }
        else {
            errMsgs.FYEndDate = null;
        }

        if (
            financialYear.fyStartDate != null &&
            financialYear.fyEndDate != null
        ) {
            const startDate = new Date(financialYear.fyStartDate);
            const endDate = new Date(financialYear.fyEndDate);

            if (endDate < startDate) {
                errMsgs.FYEndDate =
                    "End Date must be after Start Date";

                returnVal = false;
            }
        }

        setErrMessages(errMsgs);

        return returnVal;
    };

    const handleStartDateChange = (date: Date | null): void => {

        setFinancialYear({
            ...financialYear,
            fyStartDate: date
                ? date.toISOString()
                : null
        });

        setSaveErrorMsg("");
        setSaveSuccessMsg("");
    };

    const handleEndDateChange = (date: Date | null): void => {

        setFinancialYear({
            ...financialYear,
            fyEndDate: date
                ? date.toISOString()
                : null
        });

        setSaveErrorMsg("");
        setSaveSuccessMsg("");
    };

    const handleSave = async (): Promise<void> => {

        setSaveErrorMsg("");
        setSaveSuccessMsg("");

        if (!validateEntity()) {
            return;
        }

        const result =
            await accountService.updateCustomerFinancialYear(
                financialYear
            );

        if (result === true) {
            props.setCustomerFinancialYear(financialYear);
            setSaveSuccessMsg(
                "Financial year dates have been successfully saved."
            );
        }
        else {
            setSaveErrorMsg(
                "Unable to save the financial year dates."
            );
        }
    };

    return (
        <div className={`${styles.cr}`}>

            <h2
                style={{ flexGrow: 1 }}
                className={styles.listTitle}
            >
                Financial Year
            </h2>

            <div
                style={{
                    maxWidth: '600px',
                    marginRight: '10px',
                    marginLeft: '10px',
                    marginTop: '30px'
                }}
            >

                <CrDatePicker
                    label="Start Date"
                    className={styles.formField}
                    value={
                        financialYear.fyStartDate
                            ? DateService.removeTimezoneOffset(
                                new Date(financialYear.fyStartDate)
                            )
                            : null
                    }
                    onSelectDate={date => handleStartDateChange(date)}
                    required={true}
                    maxWidth="100%"
                    errorMessage={errMessages.FYStartDate}
                />

                <CrDatePicker
                    label="End Date"
                    className={styles.formField}
                    value={
                        financialYear.fyEndDate
                            ? DateService.removeTimezoneOffset(
                                new Date(financialYear.fyEndDate)
                            )
                            : null
                    }
                    minDate={
                        financialYear.fyStartDate
                            ? DateService.removeTimezoneOffset(
                                new Date(financialYear.fyStartDate)
                            )
                            : undefined
                    }
                    onSelectDate={date => handleEndDateChange(date)}
                    required={true}
                    maxWidth="100%"
                    errorMessage={errMessages.FYEndDate}
                />

                <FormButtons
                    onPrimaryClick={handleSave}
                />

                <FieldErrorMessage value={saveErrorMsg} />
                <FieldSuccessMessage value={saveSuccessMsg} />

            </div>
        </div>
    );
};